import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import CreateIcon from '@mui/icons-material/Create';
import { useLanguage } from '../context/LanguageContext';
import { useTeluguYear } from '../context/TeluguYearContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { categorizeVatu } from '../utils/krishnaYajurvedaUtils';

const PanasalaAntyakshari = () => {
  const { lang } = useLanguage();
  const { teluguYearTelugu } = useTeluguYear();
  const { user } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attendedVatus, setAttendedVatus] = useState({ junior: [], senior: [], all: [], counts: {} });
  const [batches, setBatches] = useState([]);
  const [juniorBatchCount, setJuniorBatchCount] = useState(1);
  const [seniorBatchCount, setSeniorBatchCount] = useState(1);
  const [categorizedSeniorVatus, setCategorizedSeniorVatus] = useState({ mulam: [], padam: [], kramam: [] });
  const [isCreatingBatches, setIsCreatingBatches] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Edit dialog state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [selectedVatusForEdit, setSelectedVatusForEdit] = useState([]);
  const [batchAssignments, setBatchAssignments] = useState({});
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(null);
  
  // Add new batch dialog state
  const [openAddBatchDialog, setOpenAddBatchDialog] = useState(false);
  const [newBatchType, setNewBatchType] = useState('junior');
  const [selectedVatusForNewBatch, setSelectedVatusForNewBatch] = useState([]);
  const [isAddingBatch, setIsAddingBatch] = useState(false);
  
  // Winner selection dialog state
  const [openWinnerDialog, setOpenWinnerDialog] = useState(false);
  const [batchForWinner, setBatchForWinner] = useState(null);
  const [selectedWinner, setSelectedWinner] = useState('');

  // Labels
  const labels = {
    telugu: {
      title: 'పనసల అంత్యాక్షరి',
      subtitle: 'ఈ రోజు హాజరైన విద్యార్థుల బ్యాచ్ కేటాయింపులు',
      juniorCount: 'జూనియర్ విద్యార్థుల సంఖ్య',
      seniorCount: 'సీనియర్ విద్యార్థుల సంఖ్య',
      juniorBatches: 'జూనియర్ బ్యాచ్‌ల సంఖ్య',
      seniorBatches: 'సీనియర్ బ్యాచ్‌ల సంఖ్య',
      createBatches: 'బ్యాచ్‌లను సృష్టించండి',
      noAttendedVatus: 'ఈ రోజు హాజరైన విద్యార్థులు లేరు',
      loading: 'లోడ్ అవుతోంది...',
      junior: 'జూనియర్',
      senior: 'సీనియర్',
      batch: 'బ్యాచ్',
      mulam: 'మూలం',
      padam: 'పదం',
      kramam: 'క్రమం',
      editAssignments: 'కేటాయింపులను సవరించండి',
      selectVatus: 'వటువులను ఎంచుకోండి',
      save: 'సేవ్ చేయండి',
      cancel: 'రద్దు చేయండి',
      deleteBatches: 'బ్యాచ్‌లను తొలగించండి',
      attendanceDate: 'హాజరు తేదీ',
      startBatch: 'ప్రారంభించండి',
      completeBatch: 'పూర్తి చేయండి',
      status: 'స్థితి',
      started: 'ప్రారంభమైనది',
      completed: 'పూర్తిచేయబడినది',
    },
    sanskrit: {
      title: 'पनसाला अन्त्याक्षरी',
      subtitle: 'अद्य उपस्थित वाटुनां समूह निर्योजनानि',
      juniorCount: 'कनिष्ठ वाटवः संख्या',
      seniorCount: 'वरिष्ठ वाटवः संख्या',
      juniorBatches: 'कनिष्ठ समूहानां संख्या',
      seniorBatches: 'वरिष्ठ समूहानां संख्या',
      createBatches: 'समूहान् सृजतु',
      noAttendedVatus: 'अद्य उपस्थितिः वाटवः नास्ति',
      loading: 'लोडयति...',
      junior: 'कनिष्ठ',
      senior: 'वरिष्ठ',
      batch: 'समूह',
      mulam: 'मूलम्',
      padam: 'पदम्',
      kramam: 'क्रमम्',
      editAssignments: 'निर्योजनानि संपादयतु',
      selectVatus: 'वाटून् वरणयतु',
      save: 'सँरक्षयतु',
      cancel: 'रद्दु कुरु',
      deleteBatches: 'समूहान् मार्जयतु',
      attendanceDate: 'उपस्थिति तिथिः',
      startBatch: 'आरम्भयतु',
      completeBatch: 'सम्पूर्णं कुरु',
      status: 'स्थितिः',
      started: 'आरब्धम्',
      completed: 'सम्पूर्णम्',
    }
  };

  const currentLabels = labels[lang] || labels.telugu;

  // Fetch attended vatus
  const fetchAttendedVatus = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const res = await api.get(`/api/panasala-antyakshari/attended-vatus/${teluguYearTelugu}`);
      const data = await res.json();
      
      if (res.ok && data.success) {
        setAttendedVatus(data.data);
        
        // Categorize senior vatus using categorizeVatu method
        if (data.data.senior && data.data.senior.length > 0) {
          const categorized = categorizeSeniorVatus(data.data.senior);
          setCategorizedSeniorVatus(categorized);
        }
      } else {
        setError(data.error || 'Failed to fetch attended vatus');
      }
    } catch (err) {
      console.error('Error fetching attended vatus:', err);
      setError('Failed to fetch attended vatus');
    } finally {
      setLoading(false);
    }
  }, [teluguYearTelugu]);

  // Calculate category distribution from vatus
  const calculateCategoryDistribution = (vatus) => {
    const mulam = vatus.filter(v => {
      const category = categorizeVatu(v, lang);
      return category.includes('Mulam');
    }).length;
    
    const padam = vatus.filter(v => {
      const category = categorizeVatu(v, lang);
      return category.includes('Padamu') || category.includes('Padam');
    }).length;
    
    const kramam = vatus.filter(v => {
      const category = categorizeVatu(v, lang);
      return category.includes('Kramamu') || category.includes('Kramam');
    }).length;
    
    return { mulam, padam, kramam };
  };

  // Fetch existing batches
  const fetchBatches = useCallback(async () => {
    try {
      const res = await api.get(`/api/panasala-antyakshari/batches/${teluguYearTelugu}`);
      const data = await res.json();
      
      if (res.ok && data.success) {
        // Recalculate category distribution for senior batches based on actual vatus
        const batchesWithUpdatedDistribution = data.data.map(batch => {
          if (batch.batch_type === 'senior' && batch.vatus && batch.vatus.length > 0) {
            return {
              ...batch,
              category_distribution: calculateCategoryDistribution(batch.vatus)
            };
          }
          return batch;
        });
        
        setBatches(batchesWithUpdatedDistribution);
        
        // Build assignments map from existing batches
        const assignmentsMap = {};
        batchesWithUpdatedDistribution.forEach(batch => {
          assignmentsMap[batch._id] = batch.vatu_ids || [];
        });
        setBatchAssignments(assignmentsMap);
      }
    } catch (err) {
      console.error('Error fetching batches:', err);
    }
  }, [teluguYearTelugu, lang]);

  // Categorize senior vatus into Mulam, Padam, Kramam
  const categorizeSeniorVatus = (seniorVatus) => {
    const mulam = [];
    const padam = [];
    const kramam = [];
    
    seniorVatus.forEach(vatu => {
      const category = categorizeVatu(vatu, lang);
      
      // Merge categories: Mulam-*, Padamu-*, Kramamu-* into respective groups
      if (category.includes('Mulam')) {
        mulam.push(vatu);
      } else if (category.includes('Padamu') || category.includes('Padam')) {
        padam.push(vatu);
      } else if (category.includes('Kramamu') || category.includes('Kramam')) {
        kramam.push(vatu);
      } else {
        // Default to Mulam for uncategorized
        mulam.push(vatu);
      }
    });
    
    return { mulam, padam, kramam };
  };

  // Calculate max batches based on count / 4
  const getMaxBatches = (count) => {
    return Math.max(1, Math.floor(count / 4));
  };

  // Generate dropdown options
  const generateBatchOptions = (maxBatches) => {
    const options = [];
    for (let i = 1; i <= maxBatches; i++) {
      options.push(i);
    }
    return options;
  };

  // Create batches with auto-assignment
  const handleCreateBatches = async () => {
    try {
      setIsCreatingBatches(true);
      setError('');
      
      const res = await api.post('/api/panasala-antyakshari/create-batches', {
        year: teluguYearTelugu,
        juniorBatches: juniorBatchCount,
        seniorBatches: seniorBatchCount,
        juniorVatus: attendedVatus.junior,
        seniorVatus: attendedVatus.senior,
        categorizedSeniorVatus
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        await fetchBatches();
      } else {
        setError(data.error || 'Failed to create batches');
      }
    } catch (err) {
      console.error('Error creating batches:', err);
      setError('Failed to create batches');
    } finally {
      setIsCreatingBatches(false);
    }
  };

  // Delete all batches
  const handleDeleteBatches = async () => {
    if (!window.confirm(lang === 'telugu' ? 'అన్ని బ్యాచ్‌లను తొలగించాలా?' : 'सर्वान् समूहान् मार्जयामि?')) {
      return;
    }
    
    try {
      const res = await api.delete(`/api/panasala-antyakshari/batches/${teluguYearTelugu}`);
      const data = await res.json();
      
      if (res.ok && data.success) {
        setBatches([]);
        setBatchAssignments({});
      } else {
        setError(data.error || 'Failed to delete batches');
      }
    } catch (err) {
      console.error('Error deleting batches:', err);
      setError('Failed to delete batches');
    }
  };

  // Open edit dialog for a batch
  const handleOpenEditDialog = (batch) => {
    setEditingBatch(batch);
    setSelectedVatusForEdit(batch.vatu_ids || []);
    setOpenEditDialog(true);
  };

  // Close edit dialog
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingBatch(null);
    setSelectedVatusForEdit([]);
  };

  // Get available vatus for selection (not assigned to other batches of same type)
  const getAvailableVatusForBatch = (batchType) => {
    const allVatus = batchType === 'junior' ? attendedVatus.junior : attendedVatus.senior;
    const otherBatchesOfSameType = batches.filter(b => b.batch_type === batchType && b._id !== editingBatch?._id);
    
    // Collect vatus already assigned to other batches of same type
    const assignedVatuIds = new Set();
    otherBatchesOfSameType.forEach(batch => {
      (batchAssignments[batch._id] || batch.vatu_ids || []).forEach(vatuId => {
        assignedVatuIds.add(vatuId);
      });
    });
    
    // Return vatus not assigned elsewhere (excluding currently selected)
    return allVatus.filter(v => !assignedVatuIds.has(v.vatu_id) || selectedVatusForEdit.includes(v.vatu_id));
  };

  // Handle vatu selection change
  const handleVatuSelectionChange = (event) => {
    setSelectedVatusForEdit(event.target.value);
  };

  // Remove vatu from selection
  const handleRemoveVatuFromSelection = (vatuId) => {
    setSelectedVatusForEdit(selectedVatusForEdit.filter(id => id !== vatuId));
  };

  // Save batch assignments
  const handleSaveAssignments = async () => {
    try {
      setIsSaving(true);
      
      const updatedAssignments = {
        ...batchAssignments,
        [editingBatch._id]: selectedVatusForEdit
      };
      
      const res = await api.post('/api/panasala-antyakshari/save-assignments', {
        year: teluguYearTelugu,
        assignments: updatedAssignments
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setBatchAssignments(updatedAssignments);
        await fetchBatches();
        handleCloseEditDialog();
      } else {
        setError(data.error || 'Failed to save assignments');
      }
    } catch (err) {
      console.error('Error saving assignments:', err);
      setError('Failed to save assignments');
    } finally {
      setIsSaving(false);
    }
  };

  // Update batch status
  const handleUpdateBatchStatus = async (batchId, newStatus) => {
    // If completing, open winner dialog instead
    if (newStatus === 'completed') {
      const batch = batches.find(b => b._id === batchId);
      setBatchForWinner(batch);
      setSelectedWinner('');
      setOpenWinnerDialog(true);
      return;
    }

    // For 'started' status, directly update
    try {
      setIsUpdatingStatus(batchId);
      
      const res = await api.post(`/api/panasala-antyakshari/batch/${batchId}/update-status`, {
        status: newStatus
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        await fetchBatches();
      } else {
        setError(data.error || 'Failed to update batch status');
      }
    } catch (err) {
      console.error('Error updating batch status:', err);
      setError('Failed to update batch status');
    } finally {
      setIsUpdatingStatus(null);
    }
  };

  // Handle add batch dialog open
  const handleOpenAddBatchDialog = () => {
    setNewBatchType('junior');
    setSelectedVatusForNewBatch([]);
    setOpenAddBatchDialog(true);
  };

  // Handle add batch dialog close
  const handleCloseAddBatchDialog = () => {
    setOpenAddBatchDialog(false);
    setNewBatchType('junior');
    setSelectedVatusForNewBatch([]);
  };

  // Handle winner dialog close
  const handleCloseWinnerDialog = () => {
    setOpenWinnerDialog(false);
    setBatchForWinner(null);
    setSelectedWinner('');
  };

  // Complete batch with winner selection
  const handleCompleteBatchWithWinner = async () => {
    if (!selectedWinner || !batchForWinner) {
      setError(lang === 'telugu' ? 'విజేతను ఎంచుకోండి' : 'विजेता चुनें');
      return;
    }

    try {
      setIsUpdatingStatus(batchForWinner._id);
      
      const res = await api.post(`/api/panasala-antyakshari/batch/${batchForWinner._id}/update-status`, {
        status: 'completed',
        winner: selectedWinner
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        await fetchBatches();
        handleCloseWinnerDialog();
      } else {
        setError(data.error || 'Failed to complete batch');
      }
    } catch (err) {
      console.error('Error completing batch:', err);
      setError('Failed to complete batch');
    } finally {
      setIsUpdatingStatus(null);
    }
  };

  // Get available vatus for new batch (not assigned to any batch of same type)
  const getAvailableVatusForNewBatch = () => {
    const allVatus = newBatchType === 'junior' ? attendedVatus.junior : attendedVatus.senior;
    const batchesOfSameType = batches.filter(b => b.batch_type === newBatchType);
    
    // Collect vatus already assigned to batches of same type
    const assignedVatuIds = new Set();
    batchesOfSameType.forEach(batch => {
      (batchAssignments[batch._id] || batch.vatu_ids || []).forEach(vatuId => {
        assignedVatuIds.add(vatuId);
      });
    });
    
    // Return vatus not assigned to any batch of same type
    return allVatus.filter(v => !assignedVatuIds.has(v.vatu_id));
  };

  // Handle vatu selection for new batch
  const handleNewBatchVatuSelectionChange = (event) => {
    setSelectedVatusForNewBatch(event.target.value);
  };

  // Remove vatu from new batch selection
  const handleRemoveVatuFromNewBatchSelection = (vatuId) => {
    setSelectedVatusForNewBatch(selectedVatusForNewBatch.filter(id => id !== vatuId));
  };

  // Add new batch
  const handleAddBatch = async () => {
    if (selectedVatusForNewBatch.length === 0) {
      setError(lang === 'telugu' ? 'కనీసం ఒక వటువును ఎంచుకోండి' : 'कम से कम एक वाटव को चुनें');
      return;
    }

    try {
      setIsAddingBatch(true);
      setError('');

      const res = await api.post('/api/panasala-antyakshari/add-batch', {
        year: teluguYearTelugu,
        batch_type: newBatchType,
        vatu_ids: selectedVatusForNewBatch,
        created_by: user?.id
      });

      const data = await res.json();

      if (res.ok && data.success) {
        await fetchBatches();
        handleCloseAddBatchDialog();
      } else {
        setError(data.error || 'Failed to add batch');
      }
    } catch (err) {
      console.error('Error adding batch:', err);
      setError('Failed to add batch');
    } finally {
      setIsAddingBatch(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (teluguYearTelugu) {
      fetchAttendedVatus();
      fetchBatches();
    }
  }, [teluguYearTelugu, fetchAttendedVatus, fetchBatches]);

  // Check access - Admin only
  const hasAccess = user && user.role === 'Admin';
  
  if (!hasAccess) {
    return (
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ p: 4, mt: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            {lang === 'telugu' ? 'అనుమతి లేదు' : 'अनुमति नहीं'}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {lang === 'telugu' 
              ? 'ఈ పేజీని చూడటానికి మీకు అధికారం లేదు' 
              : 'आपको यह पृष्ठ देखने की अनुमति नहीं है'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  const juniorMaxBatches = getMaxBatches(attendedVatus.counts?.junior || 0);
  const seniorMaxBatches = getMaxBatches(attendedVatus.counts?.senior || 0);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#00838f' }}>
            {currentLabels.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentLabels.subtitle}
          </Typography>
          {attendedVatus.attendanceDate && (
            <Chip 
              label={`${currentLabels.attendanceDate}: ${attendedVatus.attendanceDate}`}
              sx={{ mt: 1, backgroundColor: '#e0f7fa' }}
            />
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Step 1: Show vatu counts and batch selection */}
        {attendedVatus.counts?.total > 0 ? (
          <>
            {/* Vatu Counts */}
            <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: '#f5f5f5' }}>
              <Grid container spacing={3}>
                {/* Junior Count & Dropdown */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h6" sx={{ color: '#1976d2' }}>
                        {currentLabels.juniorCount}:
                      </Typography>
                      <Chip 
                        label={attendedVatus.counts?.junior || 0}
                        sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 600, fontSize: '1.1rem' }}
                      />
                    </Box>
                    {batches.length === 0 && attendedVatus.counts?.junior > 0 && (
                      <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>{currentLabels.juniorBatches}</InputLabel>
                        <Select
                          value={juniorBatchCount}
                          onChange={(e) => setJuniorBatchCount(e.target.value)}
                          label={currentLabels.juniorBatches}
                        >
                          {generateBatchOptions(juniorMaxBatches).map(num => (
                            <MenuItem key={num} value={num}>{num}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Grid>

                {/* Senior Count & Dropdown */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h6" sx={{ color: '#2e7d32' }}>
                        {currentLabels.seniorCount}:
                      </Typography>
                      <Chip 
                        label={attendedVatus.counts?.senior || 0}
                        sx={{ backgroundColor: '#2e7d32', color: 'white', fontWeight: 600, fontSize: '1.1rem' }}
                      />
                    </Box>
                    {batches.length === 0 && attendedVatus.counts?.senior > 0 && (
                      <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>{currentLabels.seniorBatches}</InputLabel>
                        <Select
                          value={seniorBatchCount}
                          onChange={(e) => setSeniorBatchCount(e.target.value)}
                          label={currentLabels.seniorBatches}
                        >
                          {generateBatchOptions(seniorMaxBatches).map(num => (
                            <MenuItem key={num} value={num}>{num}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Category Distribution for Senior (informational) */}
              {attendedVatus.counts?.senior > 0 && (
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #ddd' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {lang === 'telugu' ? 'సీనియర్ వర్గీకరణ:' : 'वरिष्ठ वर्गीकरणम्:'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip 
                      label={`${currentLabels.mulam}: ${categorizedSeniorVatus.mulam.length}`}
                      variant="outlined"
                      sx={{ borderColor: '#ff9800' }}
                    />
                    <Chip 
                      label={`${currentLabels.padam}: ${categorizedSeniorVatus.padam.length}`}
                      variant="outlined"
                      sx={{ borderColor: '#9c27b0' }}
                    />
                    <Chip 
                      label={`${currentLabels.kramam}: ${categorizedSeniorVatus.kramam.length}`}
                      variant="outlined"
                      sx={{ borderColor: '#00838f' }}
                    />
                  </Box>
                </Box>
              )}

              {/* Create/Delete Batches Buttons */}
              {batches.length === 0 ? (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateBatches}
                    disabled={isCreatingBatches || (attendedVatus.counts?.junior === 0 && attendedVatus.counts?.senior === 0)}
                    sx={{ backgroundColor: '#00838f', '&:hover': { backgroundColor: '#006064' } }}
                  >
                    {isCreatingBatches ? <CircularProgress size={24} /> : currentLabels.createBatches}
                  </Button>
                </Box>
              ) : (
                <Box sx={{ mt: 3, textAlign: 'center', display: 'flex', gap: 2, justifyContent: 'center' }}>
                  {batches.length > 0 && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenAddBatchDialog}
                      sx={{ backgroundColor: '#00838f', '&:hover': { backgroundColor: '#006064' } }}
                    >
                      {lang === 'telugu' ? 'కొత్త బ్యాచ్ జోడించండి' : 'नई बैच जोड़ें'}
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteBatches}
                    disabled={batches.some(b => b.status === 'started')}
                    title={batches.some(b => b.status === 'started') ? (lang === 'telugu' ? 'ప్రారంభమైన బ్యాచ్‌లను తొలగించలేరు' : 'आरंभ की गई बैच को हटाया नहीं जा सकता') : ''}
                  >
                    {currentLabels.deleteBatches}
                  </Button>
                </Box>
              )}
            </Paper>

            {/* Display Batches */}
            {batches.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                
                {/* Junior Batches */}
                {batches.filter(b => b.batch_type === 'junior').length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                      {currentLabels.junior} {currentLabels.batch}
                    </Typography>
                    <Grid container spacing={2}>
                      {batches
                        .filter(b => b.batch_type === 'junior')
                        .sort((a, b) => a.batch_number - b.batch_number)
                        .map(batch => (
                          <Grid item xs={12} sm={6} md={4} key={batch._id}>
                            <Card sx={{ backgroundColor: '#e3f2fd', position: 'relative' }}>
                              <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                                {/* Status Chip - Only show when status is started or completed */}
                                {batch.status && batch.status !== '' && (
                                  <Chip
                                    label={batch.status === 'completed' ? currentLabels.completed : currentLabels.started}
                                    size="small"
                                    sx={{
                                      backgroundColor: batch.status === 'completed' ? '#4caf50' : '#ff9800',
                                      color: 'white',
                                      fontWeight: 600,
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                )}
                                {/* Edit Button */}
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenEditDialog(batch)}
                                  title={currentLabels.editAssignments}
                                >
                                  <CreateIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <CardContent sx={{ pt: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                                  {currentLabels.junior} {currentLabels.batch} {batch.batch_number}
                                </Typography>
                                <Typography variant="caption" sx={{ mb: 2, display: 'block', fontWeight: 600 }}>
                                  {lang === 'telugu' ? 'వటువులు:' : 'वाटवः:'} {(batchAssignments[batch._id] || batch.vatu_ids || []).length}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 150, overflowY: 'auto', mb: 2 }}>
                                  {(batch.vatus || []).map(vatu => (
                                    <Box key={vatu.vatu_id} sx={{ p: 1, backgroundColor: '#fff', borderRadius: 1, borderLeft: '3px solid #1976d2' }}>
                                      <Typography variant="body2">
                                        {vatu.vatu_name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {vatu.pathasala_name}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                                {/* Status Change Buttons */}
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    size="small"
                                    variant={batch.status === 'started' ? 'contained' : 'outlined'}
                                    onClick={() => handleUpdateBatchStatus(batch._id, 'started')}
                                    disabled={isUpdatingStatus === batch._id || batch.status === 'started'}
                                    sx={{ flex: 1, fontSize: '0.75rem' }}
                                  >
                                    {currentLabels.startBatch}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant={batch.status === 'completed' ? 'contained' : 'outlined'}
                                    color="success"
                                    onClick={() => handleUpdateBatchStatus(batch._id, 'completed')}
                                    disabled={isUpdatingStatus === batch._id || batch.status === 'completed'}
                                    sx={{ flex: 1, fontSize: '0.75rem' }}
                                  >
                                    {currentLabels.completeBatch}
                                  </Button>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                )}

                {/* Senior Batches */}
                {batches.filter(b => b.batch_type === 'senior').length > 0 && (
                  <Box>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#2e7d32' }}>
                      {currentLabels.senior} {currentLabels.batch}
                    </Typography>
                    <Grid container spacing={2}>
                      {batches
                        .filter(b => b.batch_type === 'senior')
                        .sort((a, b) => a.batch_number - b.batch_number)
                        .map(batch => (
                          <Grid item xs={12} sm={6} md={4} key={batch._id}>
                            <Card sx={{ backgroundColor: '#e8f5e9', position: 'relative' }}>
                              <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                                {/* Status Chip - Only show when status is started or completed */}
                                {batch.status && batch.status !== '' && (
                                  <Chip
                                    label={batch.status === 'completed' ? currentLabels.completed : currentLabels.started}
                                    size="small"
                                    sx={{
                                      backgroundColor: batch.status === 'completed' ? '#4caf50' : '#ff9800',
                                      color: 'white',
                                      fontWeight: 600,
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                )}
                                {/* Edit Button */}
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenEditDialog(batch)}
                                  title={currentLabels.editAssignments}
                                >
                                  <CreateIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <CardContent sx={{ pt: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#2e7d32' }}>
                                  {currentLabels.senior} {currentLabels.batch} {batch.batch_number}
                                </Typography>
                                {/* Category Distribution */}
                                {batch.category_distribution && (
                                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                    <Chip 
                                      label={`${currentLabels.mulam}: ${batch.category_distribution.mulam}`}
                                      size="small"
                                      sx={{ backgroundColor: '#ff9800', color: 'white', fontSize: '0.7rem' }}
                                    />
                                    <Chip 
                                      label={`${currentLabels.padam}: ${batch.category_distribution.padam}`}
                                      size="small"
                                      sx={{ backgroundColor: '#9c27b0', color: 'white', fontSize: '0.7rem' }}
                                    />
                                    <Chip 
                                      label={`${currentLabels.kramam}: ${batch.category_distribution.kramam}`}
                                      size="small"
                                      sx={{ backgroundColor: '#00838f', color: 'white', fontSize: '0.7rem' }}
                                    />
                                  </Box>
                                )}
                                <Typography variant="caption" sx={{ mb: 2, display: 'block', fontWeight: 600 }}>
                                  {lang === 'telugu' ? 'వటువులు:' : 'वाटवः:'} {(batchAssignments[batch._id] || batch.vatu_ids || []).length}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 150, overflowY: 'auto', mb: 2 }}>
                                  {(batch.vatus || []).map(vatu => {
                                    const category = categorizeVatu(vatu, lang);
                                    return (
                                      <Box key={vatu.vatu_id} sx={{ p: 1, backgroundColor: '#fff', borderRadius: 1, borderLeft: '3px solid #2e7d32' }}>
                                        <Typography variant="body2">
                                          {vatu.vatu_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {vatu.pathasala_name} • {category}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                                {/* Status Change Buttons */}
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    size="small"
                                    variant={batch.status === 'started' ? 'contained' : 'outlined'}
                                    onClick={() => handleUpdateBatchStatus(batch._id, 'started')}
                                    disabled={isUpdatingStatus === batch._id || batch.status === 'started'}
                                    sx={{ flex: 1, fontSize: '0.75rem' }}
                                  >
                                    {currentLabels.startBatch}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant={batch.status === 'completed' ? 'contained' : 'outlined'}
                                    color="success"
                                    onClick={() => handleUpdateBatchStatus(batch._id, 'completed')}
                                    disabled={isUpdatingStatus === batch._id || batch.status === 'completed'}
                                    sx={{ flex: 1, fontSize: '0.75rem' }}
                                  >
                                    {currentLabels.completeBatch}
                                  </Button>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                )}
              </>
            )}
          </>
        ) : (
          <Alert severity="info">
            {currentLabels.noAttendedVatus}
          </Alert>
        )}
      </Paper>

      {/* Edit Assignment Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <DialogTitle sx={{ p: 0 }}>
            {currentLabels.editAssignments} - {editingBatch?.batch_type === 'junior' ? currentLabels.junior : currentLabels.senior} {currentLabels.batch} {editingBatch?.batch_number}
          </DialogTitle>
          <IconButton onClick={handleCloseEditDialog}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          {/* Vatu Multi-Select */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>{currentLabels.selectVatus}</InputLabel>
            <Select
              multiple
              value={selectedVatusForEdit}
              onChange={handleVatuSelectionChange}
              label={currentLabels.selectVatus}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((vatuId) => {
                    const allVatus = editingBatch?.batch_type === 'junior' ? attendedVatus.junior : attendedVatus.senior;
                    const vatu = allVatus.find(v => v.vatu_id === vatuId);
                    const category = vatu ? categorizeVatu(vatu, lang) : '';
                    return (
                      <Chip
                        key={vatuId}
                        label={`${vatu?.vatu_name || vatuId} - ${category}`}
                        size="small"
                        onDelete={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRemoveVatuFromSelection(vatuId);
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        sx={{ maxWidth: '100%' }}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {editingBatch && getAvailableVatusForBatch(editingBatch.batch_type)
                .filter(vatu => !selectedVatusForEdit.includes(vatu.vatu_id))
                .map(vatu => {
                  const category = categorizeVatu(vatu, lang);
                  return (
                    <MenuItem key={vatu.vatu_id} value={vatu.vatu_id}>
                      {vatu.vatu_name} - {category} ({vatu.pathasala_name})
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>

          {/* Display current selection as cards */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {lang === 'telugu' ? 'ఎంచుకున్న వటువులు:' : 'चयनित वाटवः:'}
            </Typography>
            <Grid container spacing={1}>
              {selectedVatusForEdit.map(vatuId => {
                const allVatus = editingBatch?.batch_type === 'junior' ? attendedVatus.junior : attendedVatus.senior;
                const vatu = allVatus.find(v => v.vatu_id === vatuId);
                const category = vatu ? categorizeVatu(vatu, lang) : '';
                return (
                  <Grid item xs={12} sm={6} key={vatuId}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      p: 1, 
                      backgroundColor: '#f5f5f5', 
                      borderRadius: 1,
                      borderLeft: `3px solid ${editingBatch?.batch_type === 'junior' ? '#1976d2' : '#2e7d32'}`
                    }}>
                      <Box>
                        <Typography variant="body2">{vatu?.vatu_name || vatuId}</Typography>
                        <Typography variant="caption" color="text.secondary">{category}</Typography>
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveVatuFromSelection(vatuId)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleCloseEditDialog}>
            {currentLabels.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAssignments}
            disabled={isSaving}
            sx={{ backgroundColor: '#00838f', '&:hover': { backgroundColor: '#006064' } }}
          >
            {isSaving ? <CircularProgress size={24} /> : currentLabels.save}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Batch Dialog */}
      <Dialog open={openAddBatchDialog} onClose={handleCloseAddBatchDialog} maxWidth="md" fullWidth>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <DialogTitle sx={{ p: 0 }}>
            {lang === 'telugu' ? 'కొత్త బ్యాచ్ జోడించండి' : 'नई बैच जोड़ें'}
          </DialogTitle>
          <IconButton onClick={handleCloseAddBatchDialog}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          {/* Batch Type Selection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>{lang === 'telugu' ? 'బ్యాచ్ రకం' : 'बैच प्रकार'}</InputLabel>
            <Select
              value={newBatchType}
              onChange={(e) => {
                setNewBatchType(e.target.value);
                setSelectedVatusForNewBatch([]);
              }}
              label={lang === 'telugu' ? 'బ్యాచ్ రకం' : 'बैच प्रकार'}
            >
              <MenuItem value="junior">
                {currentLabels.junior}
              </MenuItem>
              <MenuItem value="senior">
                {currentLabels.senior}
              </MenuItem>
            </Select>
          </FormControl>

          {/* Available Vatus Count */}
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {lang === 'telugu' ? 'అందుబాటులో ఉన్న వటువులు:' : 'उपलब्ध वाटव:'} {getAvailableVatusForNewBatch().length}
            </Typography>
          </Box>

          {/* Vatu Multi-Select */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>{currentLabels.selectVatus}</InputLabel>
            <Select
              multiple
              value={selectedVatusForNewBatch}
              onChange={handleNewBatchVatuSelectionChange}
              label={currentLabels.selectVatus}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((vatuId) => {
                    const allVatus = newBatchType === 'junior' ? attendedVatus.junior : attendedVatus.senior;
                    const vatu = allVatus.find(v => v.vatu_id === vatuId);
                    const category = vatu ? categorizeVatu(vatu, lang) : '';
                    return (
                      <Chip
                        key={vatuId}
                        label={`${vatu?.vatu_name || vatuId} - ${category}`}
                        size="small"
                        onDelete={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRemoveVatuFromNewBatchSelection(vatuId);
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        sx={{ maxWidth: '100%' }}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {getAvailableVatusForNewBatch()
                .filter(vatu => !selectedVatusForNewBatch.includes(vatu.vatu_id))
                .map(vatu => {
                  const category = categorizeVatu(vatu, lang);
                  return (
                    <MenuItem key={vatu.vatu_id} value={vatu.vatu_id}>
                      {vatu.vatu_name} - {category} ({vatu.pathasala_name})
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>

          {/* Display current selection as cards */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {lang === 'telugu' ? 'ఎంచుకున్న వటువులు:' : 'चयनित वाटवः:'} ({selectedVatusForNewBatch.length})
            </Typography>
            <Grid container spacing={1}>
              {selectedVatusForNewBatch.map(vatuId => {
                const allVatus = newBatchType === 'junior' ? attendedVatus.junior : attendedVatus.senior;
                const vatu = allVatus.find(v => v.vatu_id === vatuId);
                const category = vatu ? categorizeVatu(vatu, lang) : '';
                return (
                  <Grid item xs={12} sm={6} key={vatuId}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      p: 1, 
                      backgroundColor: '#f5f5f5', 
                      borderRadius: 1,
                      borderLeft: `3px solid ${newBatchType === 'junior' ? '#1976d2' : '#2e7d32'}`
                    }}>
                      <Box>
                        <Typography variant="body2">{vatu?.vatu_name || vatuId}</Typography>
                        <Typography variant="caption" color="text.secondary">{category}</Typography>
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveVatuFromNewBatchSelection(vatuId)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleCloseAddBatchDialog}>
            {currentLabels.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={handleAddBatch}
            disabled={isAddingBatch || selectedVatusForNewBatch.length === 0}
            sx={{ backgroundColor: '#00838f', '&:hover': { backgroundColor: '#006064' } }}
          >
            {isAddingBatch ? <CircularProgress size={24} /> : (lang === 'telugu' ? 'బ్యాచ్ జోడించండి' : 'बैच जोड़ें')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Winner Selection Dialog */}
      <Dialog open={openWinnerDialog} onClose={handleCloseWinnerDialog} maxWidth="sm" fullWidth>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <DialogTitle sx={{ p: 0 }}>
            {lang === 'telugu' ? 'విజేతను ఎంచుకోండి' : 'विजेता चुनें'}
          </DialogTitle>
          <IconButton onClick={handleCloseWinnerDialog}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            {lang === 'telugu' ? 'బ్యాచ్ నుండి విజేతను ఎంచుకోండి:' : 'बैच से विजेता चुनें:'}
          </Typography>
          
          <FormControl fullWidth>
            <InputLabel>{lang === 'telugu' ? 'వటువు' : 'वाटव'}</InputLabel>
            <Select
              value={selectedWinner}
              onChange={(e) => setSelectedWinner(e.target.value)}
              label={lang === 'telugu' ? 'వటువు' : 'वाटव'}
            >
              {batchForWinner && (batchForWinner.vatus || []).map(vatu => (
                <MenuItem key={vatu.vatu_id} value={vatu.vatu_id}>
                  {vatu.vatu_name} ({vatu.pathasala_name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedWinner && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8f5e9', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                {lang === 'telugu' ? 'ఎంచుకున్న విజేత:' : 'चयनित विजेता:'}
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                {batchForWinner?.vatus?.find(v => v.vatu_id === selectedWinner)?.vatu_name}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleCloseWinnerDialog}>
            {currentLabels.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={handleCompleteBatchWithWinner}
            disabled={!selectedWinner || isUpdatingStatus === batchForWinner?._id}
            sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
          >
            {isUpdatingStatus === batchForWinner?._id ? <CircularProgress size={24} /> : (lang === 'telugu' ? 'పూర్తి చేయండి' : 'पूर्ण करें')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PanasalaAntyakshari;
